// apps/client/src/world/fx.ts — look-demo town.ts ambience: chimney smoke, butterflies over the flowers and sparkles on the sea shelf.
// Three draw calls in all: smoke and sparkles are one Points cloud each (a small shader for per-point size and fade that bends with the
// curved world), butterflies one InstancedMesh.
import { rngFrom } from '@whiskerton/shared';
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { LightValues } from '../engine/lighting';
import { patchObject, worldUniforms } from '../engine/shader/worldPatch';

export interface Fx {
  readonly group: THREE.Group;
  setChimneys(points: readonly THREE.Vector3[]): void;
  /** Flower-cluster centres the butterflies circle (decor's `userData.flowerSpots`). */
  setFlowers(points: readonly THREE.Vector3[]): void;
  /** Water-surface points the sparkles twinkle around (the sea shelf). */
  setSparkles(points: readonly THREE.Vector3[]): void;
  /** `light` (optional) tints the smoke and dims the glints with the clock lighting. */
  update(dt: number, light?: LightValues): void;
}

const PUFFS = 7, FLIES = 12, SPARKS = 160;
const WHITE = new THREE.Color('#FFFFFF');
function alphaTexture(f: (x: number, y: number) => number, n = 64): THREE.DataTexture {
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    const u = (x + 0.5) / n * 2 - 1, v = (y + 0.5) / n * 2 - 1;
    data.set([255, 255, 255, Math.round(Math.min(1, Math.max(0, f(u, v))) * 255)], (y * n + x) * 4);
  }
  const t = new THREE.DataTexture(data, n, n);
  t.magFilter = t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}
const smooth = (e0: number, e1: number, x: number) => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
/** Soft puff: opaque core easing out to nothing at the rim (look demo's radial gradient). */
const puffTexture = () => alphaTexture((u, v) => { const r = Math.hypot(u, v); return r < 0.55 ? 1 - (r / 0.55) * 0.35 : 0.65 * (1 - smooth(0.55, 1, r)); });
/** Glint: a small glow plus a four-point star. */
const starTexture = () => alphaTexture((u, v) => {
  const r = Math.hypot(u, v), glow = 1 - smooth(0, 0.25, r), star = Math.max(0, 1 - Math.abs(u) * 12 - Math.abs(v) * 1.1, 1 - Math.abs(v) * 12 - Math.abs(u) * 1.1);
  return Math.max(glow, star * 0.9);
});

/** Points with per-point size (world units) and alpha, bent like the world (worldUniforms) so they stay on their chimneys and waves. */
interface Cloud { points: THREE.Points; pos: THREE.BufferAttribute; size: THREE.BufferAttribute; alpha: THREE.BufferAttribute; tint: THREE.Color }
function pointCloud(capacity: number, map: THREE.Texture, additive: boolean): Cloud {
  const geo = new THREE.BufferGeometry();
  const pos = new THREE.BufferAttribute(new Float32Array(capacity * 3), 3), size = new THREE.BufferAttribute(new Float32Array(capacity), 1);
  const alpha = new THREE.BufferAttribute(new Float32Array(capacity), 1);
  for (const a of [pos, size, alpha]) a.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('position', pos); geo.setAttribute('aSize', size); geo.setAttribute('aAlpha', alpha);
  geo.setDrawRange(0, 0);
  const uScale = { value: 400 }, uTint = { value: new THREE.Color('#FFFFFF') };
  const mat = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    uniforms: { map: { value: map }, uCurve: worldUniforms.uCurve, uCamXZ: worldUniforms.uCamXZ, uScale, uTint },
    vertexShader: `uniform float uCurve; uniform vec2 uCamXZ; uniform float uScale; attribute float aSize; attribute float aAlpha; varying float vAlpha;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vec2 d = wp.xz - uCamXZ;
        wp.y -= dot(d, d) * uCurve;
        vec4 mv = viewMatrix * wp;
        gl_Position = projectionMatrix * mv;
        gl_PointSize = aSize * uScale / max(0.05, -mv.z);
        vAlpha = aAlpha;
      }`,
    fragmentShader: `uniform sampler2D map; uniform vec3 uTint; varying float vAlpha;
      void main() {
        vec4 t = texture2D(map, gl_PointCoord);
        gl_FragColor = vec4(t.rgb * uTint, t.a * vAlpha);
        #include <colorspace_fragment>
      }`,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  const v = new THREE.Vector2();
  points.onBeforeRender = (renderer, _scene, camera) => {     // world-size points: half the buffer height × the projection's focal term
    uScale.value = renderer.getDrawingBufferSize(v).y * 0.5 * camera.projectionMatrix.elements[5]!;
  };
  return { points, pos, size, alpha, tint: uTint.value };
}

export function createFx(): Fx {
  const group = new THREE.Group();
  group.name = 'fx';
  const rng = rngFrom('fx');
  let t = 0;

  // Chimney smoke: seven soft puffs per chimney rising, growing and fading (look demo).
  let smoke = pointCloud(1, puffTexture(), false);
  let puffs: { origin: THREE.Vector3; phase: number }[] = [];
  group.add(smoke.points);

  // Butterflies: two flat wings and a dark body; flapping squashes the wings across the body.
  const wing = new THREE.CircleGeometry(0.11, 10).scale(1, 0.75, 1).rotateX(-Math.PI / 2);
  const wings = mergeGeometries([wing.clone().translate(0.1, 0, 0), wing.clone().translate(-0.1, 0, 0)])!;
  const body = new THREE.CapsuleGeometry(0.018, 0.08, 3, 6).rotateX(Math.PI / 2);
  const paint = (g: THREE.BufferGeometry, hex: string) => {
    const c = new THREE.Color(hex), n = g.attributes.position!.count, col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) col.set([c.r, c.g, c.b], i * 3);
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.deleteAttribute('uv');
    return g;
  };
  const flyGeo = mergeGeometries([paint(wings, '#FFFFFF'), paint(body.toNonIndexed(), '#4A3A2C')].map((g) => (g.index ? g.toNonIndexed() : g)))!;
  const flies = new THREE.InstancedMesh(flyGeo, new THREE.MeshStandardMaterial({ vertexColors: true, side: THREE.DoubleSide, roughness: 0.6, metalness: 0 }), FLIES);
  flies.count = 0;
  flies.frustumCulled = false;
  flies.name = 'butterflies';
  const palette = ['#F6C85F', '#FFFFFF', '#9FD8F1', '#F49A7A', '#C9B3E6'] as const;
  for (let i = 0; i < FLIES; i++) flies.setColorAt(i, new THREE.Color(rng.pick(palette)));
  patchObject(flies);
  group.add(flies);
  let flyAt: { at: THREE.Vector3; p: number; last: THREE.Vector3; yaw: number }[] = [];

  // Sparkles: glints that twinkle on the sea near the shore, each hopping to a new spot when its twinkle ends.
  const sparks = pointCloud(SPARKS, starTexture(), true);
  group.add(sparks.points);
  let spots: readonly THREE.Vector3[] = [];
  const glints = Array.from({ length: SPARKS }, () => ({ p: rng.next(), speed: 0.35 + rng.next() * 0.35, at: new THREE.Vector3() }));
  const hop = (g: (typeof glints)[number]) => {
    const s = spots[Math.floor(rng.next() * spots.length)];
    if (s) g.at.set(s.x + (rng.next() - 0.5), s.y + 0.03, s.z + (rng.next() - 0.5));
  };

  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0), pos = new THREE.Vector3();
  return {
    group,
    setChimneys(points) {
      puffs = points.flatMap((origin) => Array.from({ length: PUFFS }, (_, i) => ({ origin: origin.clone(), phase: i / PUFFS + rng.next() * 0.05 })));
      if (puffs.length > smoke.pos.count) {                       // grow the cloud
        group.remove(smoke.points);
        smoke.points.geometry.dispose();
        const map = (smoke.points.material as THREE.ShaderMaterial).uniforms.map!.value as THREE.Texture;
        (smoke.points.material as THREE.ShaderMaterial).dispose();   // the puff texture carries over to the bigger cloud
        smoke = pointCloud(Math.max(puffs.length, smoke.pos.count * 2), map, false);
        group.add(smoke.points);
      }
      smoke.points.geometry.setDrawRange(0, puffs.length);
    },
    setFlowers(points) {
      flyAt = points.length ? Array.from({ length: FLIES }, (_, i) => ({ at: points[Math.floor((i * 7919) % points.length)]!.clone(), p: rng.next() * 10, last: new THREE.Vector3(), yaw: 0 })) : [];
      flies.count = flyAt.length;
    },
    setSparkles(points) {
      spots = points;
      for (const g of glints) hop(g);
      sparks.points.geometry.setDrawRange(0, spots.length ? SPARKS : 0);
    },
    update(dt, light) {
      t += dt;
      if (light) {
        smoke.tint.copy(light.hemiSky).lerp(WHITE, 0.4).multiplyScalar(Math.min(1, 0.45 + light.hemiIntensity * 0.25));
        sparks.tint.setScalar(Math.min(1, 0.25 + light.sunIntensity * 0.3));
      }
      puffs.forEach((f, i) => {
        const k = (t * 0.2 + f.phase) % 1;
        smoke.pos.setXYZ(i, f.origin.x + Math.sin(k * 3 + f.phase * 9) * 0.18 + k * 0.7, f.origin.y + k * 2.4, f.origin.z + k * 0.25);
        smoke.size.setX(i, 0.45 + k * 1.3);
        smoke.alpha.setX(i, 0.75 * Math.pow(Math.sin(Math.PI * k), 1.2) * (k < 0.08 ? k / 0.08 : 1));
      });
      if (puffs.length) { smoke.pos.needsUpdate = true; smoke.size.needsUpdate = true; smoke.alpha.needsUpdate = true; }

      flyAt.forEach((f, i) => {
        const u = t * 0.55 + f.p;
        pos.set(f.at.x + Math.sin(u) * 1.6 + Math.sin(u * 2.7) * 0.4, f.at.y + 0.75 + Math.sin(u * 3.1) * 0.25, f.at.z + Math.cos(u * 0.8) * 1.3);
        const dx = pos.x - f.last.x, dz = pos.z - f.last.z;
        if (dx * dx + dz * dz > 1e-8) f.yaw = Math.atan2(dx, dz);
        f.last.copy(pos);
        const flap = 0.2 + 0.8 * Math.abs(Math.cos(t * 18 + f.p * 7));
        flies.setMatrixAt(i, m.compose(pos, q.setFromAxisAngle(up, f.yaw), sc.set(flap, 1, 1)));
      });
      if (flyAt.length) flies.instanceMatrix.needsUpdate = true;

      if (spots.length) {
        glints.forEach((g, i) => {
          g.p += dt * g.speed;
          if (g.p >= 1) { g.p -= 1; hop(g); }
          sparks.pos.setXYZ(i, g.at.x, g.at.y, g.at.z);
          sparks.size.setX(i, 0.42);
          sparks.alpha.setX(i, Math.pow(Math.max(0, Math.sin(Math.PI * g.p)), 6));
        });
        sparks.pos.needsUpdate = true; sparks.size.needsUpdate = true; sparks.alpha.needsUpdate = true;
      }
    },
  };
}
